import { get, has } from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import { SIZE_SELECTOR_MODES } from 'constants/pdp';
import { CAMPAIGN_NAMES } from 'constants/personalizations';
import { SHOP_IDS } from 'constants/shops';
import PdpContainer from 'containers/PdpContainer';
import Product from 'models/Product';
import * as actions from 'state/actions';
import Settings from 'utils/Settings';
import { parseQueryString } from 'utils/Url';

const PdpController = class {
  static get windowData() {
    const data = get(window, ['ALLBIRDS', 'pdpData']);

    if (!data) {
      throw new Error('Missing PDP window data.');
    }

    return data;
  }

  static get productData() {
    return get(PdpController.windowData, 'product');
  }

  static get product() {
    return new Product(PdpController.productData);
  }

  static get descriptionSingleLine() {
    return get(PdpController.windowData, 'descriptionSingleLine');
  }

  static get pdpStorytellingEnabled() {
    const isUSStore = Settings.shopID === SHOP_IDS.US;
    return isUSStore && get(PdpController.windowData, 'features.enableStorytelling', false);
  }

  static get features() {
    const contentfulManagedFeatures = get(PdpController.windowData, 'features');

    return {
      ...contentfulManagedFeatures,
      pdpStorytellingEnabled: PdpController.pdpStorytellingEnabled,
    };
  }

  static get differentiators() {
    return get(PdpController.windowData, 'differentiators');
  }

  static get urlParams() {
    return parseQueryString();
  }

  static get firstSize() {
    return get(PdpController.productData, ['sizesSortOrder', 0]);
  }

  static get size() {
    const size = get(PdpController.urlParams, 'size');

    return PdpController.sizeIsValid(size) ? size : null;
  }

  static get canonicalProductId() {
    return get(PdpController.productData, ['canonicalProduct', 'id']);
  }

  static get yotpoTestProductId() {
    return Settings.yotpoTestProductId;
  }

  static get comparisonData() {
    return get(PdpController.windowData, 'comparison');
  }

  static get sizeSelectorMode() {
    return get(PdpController.features, ['showSizeSelector'], SIZE_SELECTOR_MODES.GRID);
  }

  static get attributes() {
    return get(PdpController.windowData, 'attributes') ?? [];
  }

  static get addToCartPolicyText() {
    return get(Settings, 'addToCartPolicyText');
  }

  static get sizeChart() {
    return get(PdpController.windowData, 'sizeChart');
  }

  static get categoryCarousel() {
    return get(PdpController.windowData, 'categoryCarousel', {});
  }

  static get bulkAddButtons() {
    return get(PdpController.windowData, 'bulkAddButtons', []);
  }

  static get storyModules() {
    return get(PdpController.windowData, 'storyModules', {});
  }

  static get pageSections() {
    return get(PdpController.windowData, 'pageSections', []);
  }

  static sizeIsValid = size => has(PdpController.productData, ['sizes', size]);

  static setAdditonalPdpModuleData(store) {
    store.dispatch(
      actions.setAdditonalPdpModuleData({
        interactiveProductModule: PdpController.interactiveProductModule,
      })
    );
  }

  static setProductData(store) {
    store.dispatch(actions.setProductData(PdpController.productData));
  }

  static setCurrentSize(store) {
    let currentSize = PdpController.size;

    if (!currentSize && PdpController.sizeSelectorMode !== SIZE_SELECTOR_MODES.GRID) {
      currentSize = PdpController.firstSize;
    }

    store.dispatch(actions.setCurrentSize(currentSize));
  }

  static setCurrentProduct(store) {
    store.dispatch(actions.setCurrentProduct(get(PdpController, ['product', 'handle'])));
    store.dispatch(actions.setInitialProduct(get(PdpController, ['product', 'handle'])));
  }

  static fetchMasterCollection(store) {
    store.dispatch(actions.fetchMasterProduct(PdpController.product.master));
  }

  static fetchContentfulStoreLocation(store) {
    store.dispatch(actions.fetchContentfulStorelocation());
  }

  static mountPdpContainer = store => {
    ReactDOM.render(
      <Provider store={store}>
        <PdpContainer
          addToCartPolicyText={PdpController.addToCartPolicyText}
          attributes={PdpController.attributes}
          bulkAddOptions={PdpController.bulkAddButtons}
          categoryCarousel={PdpController.categoryCarousel}
          descriptionSingleLine={PdpController.descriptionSingleLine}
          differentiators={PdpController.differentiators}
          features={PdpController.features}
          pageSections={PdpController.pageSections}
          sizeChart={PdpController.sizeChart}
          storyModules={PdpController.storyModules}
          trackModuleEvent={PdpController.trackModuleEvent}
        />
      </Provider>,
      document.getElementById('pdp-container')
    );
  };

  static fetchDyCampaigns = store => {
    store.dispatch(
      actions.fetchPersonalizations({
        names: [
          CAMPAIGN_NAMES.PDP_ADDITIONAL_RECOMMENDATIONS,
          CAMPAIGN_NAMES.PDP_ADDITIONAL_SIZE_GUIDANCE,
          CAMPAIGN_NAMES.PDP_ADD_TO_CART_MODAL_RECS,
          CAMPAIGN_NAMES.PDP_BTF_STORY_MODULE,
          CAMPAIGN_NAMES.PDP_END_OF_PAGE_CAROUSEL_RECS,
          CAMPAIGN_NAMES.PDP_IMAGES_EXPERIMENT,
          CAMPAIGN_NAMES.PDP_LIFESTYLE_VIDEO,
          CAMPAIGN_NAMES.PDP_SHOP_PAY,
          CAMPAIGN_NAMES.PDP_SIDEBAR_RECS,
          CAMPAIGN_NAMES.PDP_STORY_MODULE_CONTENT,
        ],
      })
    );
  };

  static init = async store => {
    PdpController.setProductData(store);
    PdpController.setCurrentSize(store);
    PdpController.setCurrentProduct(store);
    PdpController.mountPdpContainer(store);
    PdpController.fetchMasterCollection(store);
    PdpController.fetchDyCampaigns(store);
    PdpController.fetchContentfulStoreLocation(store);
  };
};

export default PdpController;
